/**
 * TimeSlotPicker — unified time slot selection component.
 *
 * Use the `variant` prop to switch between the five display styles:
 *
 * - `'chips'`    – Compact pill/chip buttons laid out in a flex-wrap row.
 *                  Best for small slot counts or tight horizontal space.
 *                  (was: TimeSlotChips)
 *
 * - `'cards'`    – Full-width card rows with an icon avatar, spot count, and a
 *                  check-circle indicator on the selected card.
 *                  Best for when extra metadata (spotsLeft, originalTime) must be
 *                  visible at a glance.
 *                  (was: TimeSlotCards)
 *
 * - `'dropdown'` – A single <Select> trigger that shows available slots in a
 *                  popover. Best when vertical space is scarce or there are many
 *                  slots to choose from.
 *                  (was: TimeSlotDropdown)
 *
 * - `'grid'`     – 3-column button grid. A small check mark appears on the
 *                  selected cell. Best for dense sets of equally-weighted slots.
 *                  (was: TimeSlotGrid)
 *
 * - `'timeline'` – Vertical timeline with a connector line and dot markers.
 *                  Each slot shows a "Disponible" badge. Best for showing
 *                  chronological ordering or linear progression.
 *                  (was: TimeSlotTimeline)
 *
 * All variants accept the same props:
 *
 * ```ts
 * {
 *   variant:               TimeSlotVariant;      // required
 *   slots:                 TimeSlot[];           // required
 *   selectedTime:          string | null;        // required
 *   onSelect:              (time: string) => void; // required
 *   label:                 string;               // required
 *   showTimezoneConversion?: boolean;            // optional
 * }
 * ```
 *
 * The `TimeSlot` type (re-exported here for convenience) is:
 * ```ts
 * { time: string; available: boolean; originalTime?: string; spotsLeft?: number }
 * ```
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { Clock, User, CheckCircle2, Globe, Check } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { TimeSlot } from './TimeSlotChips';

export type { TimeSlot };

export type TimeSlotVariant = 'cards' | 'chips' | 'dropdown' | 'grid' | 'timeline';

export interface TimeSlotPickerProps {
  variant: TimeSlotVariant;
  slots: TimeSlot[];
  selectedTime: string | null;
  onSelect: (time: string) => void;
  label: string;
  showTimezoneConversion?: boolean;
}

// ---------------------------------------------------------------------------
// Shared label
// ---------------------------------------------------------------------------

function SlotLabel({ label, showTimezoneConversion }: { label: string; showTimezoneConversion?: boolean }) {
  return (
    <label className="text-sm font-medium text-foreground flex items-center gap-2">
      {label}
      {showTimezoneConversion && <Globe className="w-3 h-3 text-therapy-warning" />}
    </label>
  );
}

// ---------------------------------------------------------------------------
// Variants
// ---------------------------------------------------------------------------

function CardsVariant({ slots, selectedTime, onSelect, label, showTimezoneConversion }: Omit<TimeSlotPickerProps, 'variant'>) {
  return (
    <div className="space-y-3">
      <SlotLabel label={label} showTimezoneConversion={showTimezoneConversion} />
      <div className="space-y-2">
        {slots.map((slot) => (
          <button
            key={slot.time}
            disabled={!slot.available}
            onClick={() => slot.available && onSelect(slot.time)}
            className={cn(
              'w-full p-4 rounded-xl transition-all duration-200',
              'border flex items-center justify-between',
              selectedTime === slot.time
                ? 'bg-therapy-lavender-light border-primary shadow-md'
                : slot.available
                ? 'bg-card border-border hover:border-primary/50 hover:bg-therapy-lavender-light/50'
                : 'bg-muted/30 border-transparent cursor-not-allowed opacity-60',
              showTimezoneConversion && slot.available && 'border-therapy-warning/30 hover:border-therapy-warning'
            )}
          >
            <div className="flex items-center gap-3">
              <div
                className={cn(
                  'w-10 h-10 rounded-full flex items-center justify-center',
                  selectedTime === slot.time
                    ? 'bg-primary text-primary-foreground'
                    : showTimezoneConversion
                    ? 'bg-therapy-warning/20 text-therapy-warning'
                    : 'bg-secondary text-secondary-foreground'
                )}
              >
                {showTimezoneConversion ? <Globe className="w-5 h-5" /> : <Clock className="w-5 h-5" />}
              </div>
              <div className="text-left">
                <div className="flex items-center gap-2">
                  <span className="text-base font-semibold text-foreground">{slot.time}</span>
                  {showTimezoneConversion && slot.originalTime && (
                    <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                      {slot.originalTime} terapeuta
                    </span>
                  )}
                </div>
                {slot.spotsLeft !== undefined && slot.available && (
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <User className="w-3 h-3" />
                    {slot.spotsLeft} lugares disponibles
                  </span>
                )}
              </div>
            </div>
            {selectedTime === slot.time && <CheckCircle2 className="w-5 h-5 text-primary" />}
          </button>
        ))}
      </div>
    </div>
  );
}

function ChipsVariant({ slots, selectedTime, onSelect, label, showTimezoneConversion }: Omit<TimeSlotPickerProps, 'variant'>) {
  return (
    <div className="space-y-3">
      <SlotLabel label={label} showTimezoneConversion={showTimezoneConversion} />
      <div className="flex flex-wrap gap-2">
        {slots.map((slot) => (
          <button
            key={slot.time}
            disabled={!slot.available}
            onClick={() => slot.available && onSelect(slot.time)}
            className={cn(
              'px-4 py-2 rounded-full text-sm font-medium transition-all duration-200',
              'border-2 flex items-center gap-2',
              selectedTime === slot.time
                ? 'bg-primary text-primary-foreground border-primary shadow-lg shadow-primary/25'
                : slot.available
                ? 'bg-card border-border hover:border-primary hover:bg-therapy-lavender-light text-foreground'
                : 'bg-muted text-muted-foreground border-transparent cursor-not-allowed opacity-50',
              showTimezoneConversion && slot.available && 'border-therapy-warning/30 hover:border-therapy-warning'
            )}
          >
            <Clock className="w-3.5 h-3.5" />
            <span>{slot.time}</span>
            {showTimezoneConversion && slot.originalTime && (
              <span className="text-xs text-muted-foreground ml-1">({slot.originalTime})</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

function DropdownVariant({ slots, selectedTime, onSelect, label, showTimezoneConversion }: Omit<TimeSlotPickerProps, 'variant'>) {
  const availableSlots = slots.filter((s) => s.available);
  return (
    <div className="space-y-3">
      <SlotLabel label={label} showTimezoneConversion={showTimezoneConversion} />
      <Select value={selectedTime || undefined} onValueChange={onSelect}>
        <SelectTrigger
          className={cn(
            'w-full h-12 bg-card border-border hover:border-primary transition-colors',
            showTimezoneConversion && 'border-therapy-warning/30 hover:border-therapy-warning'
          )}
        >
          <div className="flex items-center gap-2">
            {showTimezoneConversion ? (
              <Globe className="w-4 h-4 text-therapy-warning" />
            ) : (
              <Clock className="w-4 h-4 text-muted-foreground" />
            )}
            <SelectValue placeholder="Seleccionar horario" />
          </div>
        </SelectTrigger>
        <SelectContent className="bg-popover">
          {availableSlots.map((slot) => (
            <SelectItem
              key={slot.time}
              value={slot.time}
              className={cn(
                'cursor-pointer',
                showTimezoneConversion
                  ? 'hover:bg-therapy-warning/10 focus:bg-therapy-warning/10'
                  : 'hover:bg-therapy-lavender-light focus:bg-therapy-lavender-light'
              )}
            >
              <div className="flex items-center gap-2">
                <span className="font-medium">{slot.time}</span>
                {showTimezoneConversion && slot.originalTime && (
                  <span className="text-xs text-muted-foreground">({slot.originalTime} terapeuta)</span>
                )}
                <span className="text-xs text-therapy-success">● Disponible</span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function GridVariant({ slots, selectedTime, onSelect, label, showTimezoneConversion }: Omit<TimeSlotPickerProps, 'variant'>) {
  return (
    <div className="space-y-3">
      <SlotLabel label={label} showTimezoneConversion={showTimezoneConversion} />
      <div className="grid grid-cols-3 gap-2">
        {slots.map((slot) => (
          <button
            key={slot.time}
            disabled={!slot.available}
            onClick={() => slot.available && onSelect(slot.time)}
            className={cn(
              'relative py-3 px-4 rounded-lg text-sm font-semibold transition-all duration-200',
              'border flex flex-col items-center justify-center',
              selectedTime === slot.time
                ? 'bg-primary text-primary-foreground border-primary ring-2 ring-primary/30'
                : slot.available
                ? 'bg-card border-border hover:border-primary hover:shadow-md text-foreground'
                : 'bg-muted/50 text-muted-foreground border-transparent cursor-not-allowed',
              showTimezoneConversion && slot.available && 'border-therapy-warning/30 hover:border-therapy-warning'
            )}
          >
            <span>{slot.time}</span>
            {showTimezoneConversion && slot.originalTime && (
              <span className="text-xs text-muted-foreground mt-0.5">{slot.originalTime} origen</span>
            )}
            {selectedTime === slot.time && <Check className="absolute top-1 right-1 w-3.5 h-3.5" />}
          </button>
        ))}
      </div>
    </div>
  );
}

function TimelineVariant({ slots, selectedTime, onSelect, label, showTimezoneConversion }: Omit<TimeSlotPickerProps, 'variant'>) {
  return (
    <div className="space-y-3">
      <SlotLabel label={label} showTimezoneConversion={showTimezoneConversion} />
      <div className="relative">
        <div className={cn('absolute left-4 top-0 bottom-0 w-0.5', showTimezoneConversion ? 'bg-therapy-warning/30' : 'bg-border')} />
        <div className="space-y-1">
          {slots.map((slot) => (
            <button
              key={slot.time}
              disabled={!slot.available}
              onClick={() => slot.available && onSelect(slot.time)}
              className={cn(
                'relative w-full pl-10 pr-4 py-2 text-left transition-all duration-200',
                'rounded-lg flex items-center gap-3',
                selectedTime === slot.time
                  ? showTimezoneConversion
                    ? 'bg-therapy-warning/10'
                    : 'bg-therapy-lavender-light'
                  : slot.available
                  ? 'hover:bg-muted/50'
                  : 'opacity-40 cursor-not-allowed'
              )}
            >
              <div
                className={cn(
                  'absolute left-2.5 w-3 h-3 rounded-full border-2 transition-all',
                  selectedTime === slot.time
                    ? showTimezoneConversion
                      ? 'bg-therapy-warning border-therapy-warning scale-125'
                      : 'bg-primary border-primary scale-125'
                    : slot.available
                    ? showTimezoneConversion
                      ? 'bg-card border-therapy-warning'
                      : 'bg-card border-primary'
                    : 'bg-muted border-muted-foreground'
                )}
              />
              <span
                className={cn(
                  'text-sm font-medium',
                  selectedTime === slot.time
                    ? showTimezoneConversion
                      ? 'text-therapy-warning'
                      : 'text-primary'
                    : 'text-foreground'
                )}
              >
                {slot.time}
              </span>
              {showTimezoneConversion && slot.originalTime && (
                <span className="text-xs text-muted-foreground">({slot.originalTime})</span>
              )}
              {slot.available && (
                <span
                  className={cn(
                    'text-xs px-2 py-0.5 rounded-full ml-auto',
                    selectedTime === slot.time
                      ? showTimezoneConversion
                        ? 'bg-therapy-warning text-white'
                        : 'bg-primary text-primary-foreground'
                      : 'bg-secondary text-secondary-foreground'
                  )}
                >
                  Disponible
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Unified component
// ---------------------------------------------------------------------------

const VARIANT_MAP: Record<TimeSlotVariant, React.FC<Omit<TimeSlotPickerProps, 'variant'>>> = {
  cards: CardsVariant,
  chips: ChipsVariant,
  dropdown: DropdownVariant,
  grid: GridVariant,
  timeline: TimelineVariant,
};

export function TimeSlotPicker({ variant, ...rest }: TimeSlotPickerProps) {
  const Variant = VARIANT_MAP[variant];
  return <Variant {...rest} />;
}
